import React, { useState } from 'react';
import { HashRouter as Router, Route, Switch, Redirect} from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { i18nIntl, NSchange, getRoundLabel, getRemotedbUrl } from '../lib/ep.js';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import { CreateButton, SquaredButton } from '../components/builder/ActionButton';
import ActionLink from '../components/builder/ActionLink';
import BuilderCanvas from '../components/builder/BuilderCanvas';
import BuilderSidebar from '../components/builder/BuilderSidebar';
import ButtonCollection from '../components/builder/ButtonCollection.js';
import RadioGroup from '../components/builder/RadioGroup';
import { DeleteButton } from '../components/builder/ConfirmButton';
import DesignWarningMessage from '../components/builder/DesignWarningMessage';
import FloatingLayer from '../components/builder/FloatingLayer';
import Input from '../components/builder/Input';
import MediaUploader from '../components/builder/MediaUploader';
import Model from '../components/builder/Model';
import { NavigationTabsBuilder } from '../components/builder/NavigationTabs';
import RichEditor from '../components/builder/RichEditor';
import SaveButton from '../components/builder/SaveButton';
import SectionTitle from '../components/builder/SectionTitle';
import SectionSubtitle from '../components/builder/SectionSubtitle';
import Select from '../components/builder/Select';
import TextArea from '../components/builder/TextArea';
import ToggleButton from '../components/builder/ToggleButton';
import ToggleSectionButton from '../components/builder/ToggleSectionButton';

import PromotionBracketsMosaic from '../components/users/PromotionBracketsMosaic';

import Layout from '../components/front/Layout';

import ContainerSliders from '../components/backend/elements/ContainerSliders';

import BracketsEdit from './BracketsEdit';
import StageManagerEdit from './StageManagerEdit';

import transferPlaceholder from '../hoc/transferPlaceholder';
import Tooltip from '../components/builder/Tooltip.js';
import BuilderMain, { BuilderMainWithoutSidebar } from '../components/builder/BuilderMain.js';
const TextAreaPlaceHolder = injectIntl(transferPlaceholder(TextArea));
const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const DragHandle = SortableHandle(() => <div class="sorter_target" />);

const SortableFields = SortableContainer(({ items, data }) => {
	const i18n = (m, c) => i18nIntl(m, data.intl, c);

	return (
		<div>
			<ul class="gui_sortable_table">
				<li class="gui_sortable_table_header">
					<div style={{flexGrow:'1'}}>{i18n("Title")}</div>

					<div class="content" style={{width:'20%'}}>{i18n("Number of candidates")}</div>

					<div class="content" style={{width:'20%'}}>{i18n("Status")}</div>

					<div class="content" style={{width:'20%'}}>{i18n("Winner")}</div>

					<div class="gui_text_align_end" style={{width:'180px'}}>{i18n("Actions")}</div>
				</li>
			</ul>

			<ul class="gui_sortable_table">
				{items.map((br, b) => {
					return (
						<SortableItem
							key={`bracket_${b}`}
							index={b}
							bracket={br?.Bracket}
							{...data}
						/>
					)
				})}
			</ul>
		</div>
	);
});

const SortableItem = SortableElement((props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const bracket = props.bracket;
	const candidates = props.candidates ?  props.candidates.filter((c) => c.BracketCandidate.bracket_id == bracket.id) : [];

	let edit_link = `#stages/brackets/${bracket.id}/`;
	if(bracket.current_round > 0) {
		edit_link += `rounds/${Math.min(parseInt(bracket.current_round), parseInt(bracket.num_rounds))}`;
	} else {
		edit_link += 'candidates';
	}

	const is_sortable = (props.is_sortable != undefined && props.num_brackets > 1) ? props.is_sortable : false;

	let has_bracket_started = true;
	let bracket_status_label;
	let bracket_winner = "-";
	switch(parseInt(bracket.current_round)) {
		case 0:
			has_bracket_started = false;
			bracket_status_label = i18n("Under construction");
			break;
		case (parseInt(bracket.num_rounds) + 1):
			const winner = candidates.find((obj) => { return obj.BracketCandidate.round == bracket.current_round; });
			bracket_status_label = i18n("Finalized");
			bracket_winner = winner.BracketCandidate.name;
			break;
		default:
			bracket_status_label = i18n("Active") + " (" + getRoundLabel(bracket, bracket.current_round, false, props.intl) + ")";
			break;
	}

	return (
		<li class="gui_sortable_table_item">
			{(is_sortable) && (
				<DragHandle />
			)}

			<div style={{flexGrow:'1'}}>
				<strong class="gui_pos_rel gui_flexbox" style={{height:'1.5em'}}>
					<ActionLink _class={{other:'gui_cutTextOnOverflow'}} _href={edit_link}>{bracket.title}</ActionLink>
				</strong>
			</div>

			<div class="content" style={{width:'20%'}}>
				{candidates.length}/{Math.pow(2, bracket.num_rounds)}
			</div>

			<div class="content" style={{width:'20%'}}>
				{bracket_status_label}
			</div>

			<div class="content" style={{width:'20%'}}>
				{bracket_winner}
			</div>

			<ButtonCollection _class={{other:'gui_flex_end'}} style={{width:'180px'}}>
				{(has_bracket_started) && (
					<ToggleButton
						squared={true}
						active={bracket.status == '1'}
						icon='knk-visible'
						icon_off='knk-invisible redIcon'
						onClick={() => props.toggle_bracket(bracket.id)}
						tooltip={<Tooltip with_icon={false} position="left">{(bracket.status == '1') ? "The bracket is visible" : "The bracket is NOT visible"}</Tooltip>}
						aria-label="Toggle bracket visibility"
					/>
				)}

				<SquaredButton
					_href={edit_link}
					icon='knk-edit'
					tooltip={<Tooltip with_icon={false} position="left">Edit bracket</Tooltip>}
					aria-label="Edit"
				/>

				<DeleteButton
					squared={true}
					disabled={(has_bracket_started) ? true : false}
					onClick={(!has_bracket_started) ? () => props.delete_bracket(bracket.id) : null}
					_position="top"
					tooltip={<Tooltip with_icon={false} position="left">{(!has_bracket_started) ? "Delete bracket" : "The bracket can't be deleted since it already started"}</Tooltip>}
					aria-label={(!has_bracket_started) ? "Delete" : null}
				/>
			</ButtonCollection>
		</li>
	)
});

const BracketsBuilder = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_new_bracket = {
		title:"",
		num_rounds:null,
	};

	const default_new_final = {
		title:"",
		bracket_1:null,
		bracket_2:null,
	};

	const [ promotion, setPromotion ] = useState(props.promotion);
	const [ redirect_to, setReditectTo ] = useState(false);
	const [ display_create_bracket_layer, setDisplayCreateBracketLayer ] = useState(false);
	const [ display_create_final_layer, setDisplayCreateFinalLayer ] = useState(false);
	const [ new_bracket, setNewBracket ] = useState(default_new_bracket);
	const [ new_final, setNewFinal ] = useState(default_new_final);

	const changeBracket = (k, v) => {
		setNewBracket({ ...new_bracket, [k]:v });
	};

	const changeFinal = (k, v) => {
		setNewFinal({ ...new_final, [k]:v });
	};

	const simplePromoChange = (k, v) => {
		setPromotion({ ...promotion, [k]:v });
		NSchange(props.promotion, k, v);
	};

	const savePromo = (cb) => {
		props.promoModel.save((r) => {
			if(cb != undefined) cb();
		});
	};

	const createBracket = () => {
		const _new_bracket = { ...new_bracket, pos:props.brackets.brackets.length };
		setNewBracket(_new_bracket);

		$.post(getRemotedbUrl(`/stages/save_bracket/${promotion.id}`), {
			bracket:JSON.stringify(_new_bracket),
		}, (res) => {
			props.dispatch({ type:'ADD_BRACKET', bracket:res });

			setDisplayCreateBracketLayer(false);
			setNewBracket(default_new_bracket);

			setReditectTo(`/stages/brackets/${bracket.Bracket.id}/candidates`);
		}, 'JSON');
	}

	const createFinal = () => {
		$.post(getRemotedbUrl(`/stages/generate_final/${promotion.id}`), {
			bracket:JSON.stringify(new_final),
		}, (res) => {
			props.dispatch({ type:'ADD_BRACKET', bracket:res.bracket});
			props.dispatch({ type:'ADD_CANDIDATE', candidates:res.candidates });
			props.stageStore.dispatch({ type:'ADD_STAGES', stages:res.stages });
			props.questionsStore.dispatch({type:'ADDQUESTION', questions:res.questions });
			props.dispatch({ type:'ADD_MATCH', match:res.matches });

			setDisplayCreateFinalLayer(false);
			setNewFinal(default_new_final);

			setReditectTo(`/stages/brackets/${res.bracket.Bracket.id}/candidates`);
		}, 'JSON');
	};

	const delete_bracket = (id) => {
		$.post(getRemotedbUrl(`/stages/delete_bracket/${promotion.id}`), { id }, 'JSON');
		props.dispatch({ type:'REMOVE_BRACKET', id });
	};

	const toggle_bracket = (id) => {
		const bracket = props.brackets.brackets.find((b) => (b.Bracket.id == id));
		bracket.Bracket.status = (bracket.Bracket.status == 0) ? 1 : 0;
		props.dispatch({ type:'CHANGE_BRACKET', id, k:'status', v:bracket.Bracket.status });

		$.post(getRemotedbUrl(`/stages/save_bracket/${promotion.id}`), {
			bracket:JSON.stringify(bracket),
		});
	};

	const onSortEnd = ({ oldIndex, newIndex }) => {
		const brackets = arrayMove(props.brackets.brackets, oldIndex, newIndex);

		const data = {};
		for(let i in brackets) {
			data[brackets[i].Bracket.id] = i;
		}

		props.dispatch({ type:'SORT_BRACKETS', order:data });

		$.post(getRemotedbUrl(`/stages/order_brackets/${promotion.id}`), { order:data });
	};

	const togglePublicBrackets = () => {
		const new_state = (props.promotion?.brackets_enabled ?? false) ? false : true;
		simplePromoChange('brackets_enabled', new_state);
		savePromo();
	};

	const basename = 'stages/brackets';

	const num_candidates_in_bracket = [
		{ k:"", v:`-- ${i18n("Select an option")} --` },
		{ k:2, v:`4 ${i18n("Candidates", 'lowercase')} + (2 ${i18n("Rounds", 'lowercase')})` },
		{ k:3, v:`8 ${i18n("Candidates", 'lowercase')} + (3 ${i18n("Rounds", 'lowercase')})` },
		{ k:4, v:`16 ${i18n("Candidates", 'lowercase')} + (4 ${i18n("Rounds", 'lowercase')})` },
		{ k:5, v:`32 ${i18n("Candidates", 'lowercase')} + (5 ${i18n("Rounds", 'lowercase')})` },
		{ k:6, v:`64 ${i18n("Candidates", 'lowercase')} + (6 ${i18n("Rounds", 'lowercase')})` },
	];

	const enabled_create = (new_bracket.num_rounds > 0 && (new_bracket?.title ?? '').length >= 2 && new_bracket.type != undefined) ? true : false;

	const enabled_create_final = ((new_final?.title ?? '').length >= 2 && new_final.bracket_1 != undefined && new_final.bracket_2 != undefined && new_final.bracket_1 != new_final.bracket_2 && new_final.type != undefined) ? true : false;

	if(redirect_to != false) {
		return (
			<Redirect to={redirect_to} />
		)
	}

	const sections = [
		{ label:"Brackets list", link:basename, icon:'knk-view-compressed' },
		{ label:i18n("Brackets page"), link:`${basename}/layout`, icon:'knk-profile' },
	];
	if(promotion.promotion_type_id == '35') sections.push({ label:"Voting page", link:`${basename}/manager/1`, icon:'knk-quiz' });
	if(promotion.promo_type == '2') sections.push({ label:"Advanced", link:`${basename}/advanced`, icon:'knk-script' });

	const bracket_types = [
		{ value:1, label:"By the users votes" },
		{ value:0, label:"By the score of a real matchup" },
	];

	return (
		<>
			<Router hashType="noslash" basename={basename}>
				<Switch>
					<Route exact={true} path={'/'} render={(route) => {
						const num_brackets_ended_arr = (props?.brackets?.brackets ?? []).filter((obj) => {
							return (parseInt(obj?.Bracket.current_round) > parseInt(obj?.Bracket.num_rounds))
						});

						const num_brackets_ended = [{ k:'', v:`-- ${i18n("Select a bracket")} --` }];
						num_brackets_ended_arr.map((bracket) => {
							num_brackets_ended.push({ k:bracket.Bracket.id, v:bracket.Bracket.title });
						});

						return (
							<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true}>
								<NavigationTabsBuilder
									active={`/${basename}${route.location.pathname}`}
									sections={sections}
								/>

								{(props.brackets.brackets.length > 0) && (
									<section class="not_awesome">
										<div class="gui_section_header alone_section_header cleared_header">
											<SectionTitle _class={{ other:'in_top' }}>Brackets</SectionTitle>

											{(num_brackets_ended.length > 2) && (
												<CreateButton
													onClick={() => setDisplayCreateFinalLayer(true)}
												>Create new final</CreateButton>
											)}

											<CreateButton
												onClick={() => setDisplayCreateBracketLayer(true)}
											>Create bracket</CreateButton>
										</div>

										<div class="gui_table_with_graphic">
											<div class="gui_not_in_top_huge in_bottom">
												<SortableFields
													items={props.brackets.brackets}
													data={{
														intl:props.intl,
														num_brackets:props.brackets.brackets.length,
														is_sortable:true,
														candidates:props.brackets.candidates,
														delete_bracket,
														toggle_bracket,
													}}
													onSortStart={() => preventSelection(true)}
													onSortEnd={onSortEnd}
													useDragHandle={true}
												/>
											</div>
										</div>
									</section>
								)}

								{(props.brackets.brackets.length == 0) && (
									<section class="not_awesome">
										<div class="gui_section_header alone_section_header cleared_header">
											<SectionTitle
												_class={{other:'in_top'}}
											>Brackets</SectionTitle>

											<CreateButton
												onClick={() => setDisplayCreateBracketLayer(true)}
											>Create your first bracket</CreateButton>
										</div>

										<SectionSubtitle>Brackets is a competition with direct elimination in multiple rounds. It begins with the first round of matchups between candidates. The loser of a matchup is directly eliminated and the winner advances to the next round. The rounds are played and in each round, half of the candidates are eliminated until there is only one candidate standing that is announced as the winner.</SectionSubtitle>

										<SectionSubtitle>The users of the promotion participate by voting for the candidate they predict to be the winner of the matchups of each round and earn points for each correct prediction.</SectionSubtitle>

										<article class="gui_not_in_top_huge">
											<div>
												<strong class="block_title">{i18n("The Easypromos bracket tool allows you to")}:</strong>

												<div>
													<ul class="bulleted_list">
														<li>{i18n("Create and customize brackets with 64, 32, 16, 8 or 4 candidates.")}</li>

														<li>{i18n("Define the matchup of the first round manually or randomly.")}</li>

														<li>{i18n("Create the bracket overview and the progress, 100% mobile compatible.")}</li>

														<li>{i18n("Automatically generate the voting page with the matchups of each round.")}</li>

														<li>{i18n("Select the winner of each matchup, based on the participants' votes or other criteria (example: result of a real sporting event).")}</li>

														<li>{i18n("Automatically update the score of the participants based on their correct predictions.")}</li>

														<li>{i18n("Publish a page with the progress of the candidates in the bracket.")}</li>

														<li>{i18n("Publish a page with the ranking of the participants based on their score.")}</li>
													</ul>
												</div>
											</div>

											<div class="gui_not_in_top_huge">
												<strong class="block_title">{i18n("In addition")}:</strong>

												<div>
													<ul class="bulleted_list">
														<li>{i18n("Multibracket support for tournaments with conferences, quadrant divisions or with more than 64 candidates.")}</li>

														<li>{i18n("Byes support for top candidates or for brackets with a number of candidates different than 64, 32, 16, 8 and 4.")}</li>
													</ul>
												</div>
											</div>
										</article>
									</section>
								)}

								{(display_create_bracket_layer !== false) && (
									<FloatingLayer
										_class={{ size:'small' }}
										backButton={() => {
											setDisplayCreateBracketLayer(false);
											setNewBracket(false);
										}}
									>
										<div>
											<SectionTitle>Brackets</SectionTitle>

											<Model model={new_bracket} change={changeBracket}>
												<Input
													name="title"
													_class={{first:true, size:60}}
													max_length={50}
												>***Name</Input>

												<Select
													name="num_rounds"
													_class={{size:60}}
													values={num_candidates_in_bracket}
												>Number of candidates</Select>

												<RadioGroup
													elements={bracket_types}
													name="type"
													change_cb={(e) => changeBracket(e)}
												>How will you settle the matchup?</RadioGroup>
											</Model>

											<ButtonCollection in_floating={true}>
												<SaveButton
													type='create'
													disabled={!enabled_create}
													onClick={(e, cb) => createBracket(cb)}
												>Create bracket</SaveButton>
											</ButtonCollection>
										</div>
									</FloatingLayer>
								)}

								{(display_create_final_layer !== false) && (
									<FloatingLayer
										_class={{ size:'small' }}
										backButton={() => {
											setDisplayCreateFinalLayer(false);
											setNewFinal(false);
										}}
									>
										<div>
											<SectionTitle>New final</SectionTitle>

											<Model model={new_final} change={changeFinal}>
												<Input
													name="title"
													_class={{ first:true, size:60 }}
													max_length={50}
												>***Name</Input>

												<Select
													name="bracket_1"
													_class={{size:60}}
													values={num_brackets_ended.filter((b) => (new_final.bracket_2 != b.k || b.k == '') )}
													autotranslate={false}
												>{i18n("Bracket")} #1</Select>

												<Select
													name="bracket_2"
													_class={{size:60}}
													values={num_brackets_ended.filter((b) => (new_final.bracket_1 != b.k || b.k == '') )}
													autotranslate={false}
												>{i18n("Bracket")} #2</Select>

												<RadioGroup
													elements={bracket_types}
													name="type"
												>How will you settle the matchup?</RadioGroup>
											</Model>

											<ButtonCollection in_floating={true}>
												<SaveButton
													disabled={!enabled_create_final}
													color="green"
													onClick={(e, cb) => createFinal(cb)}
												>Create final</SaveButton>
											</ButtonCollection>
										</div>
									</FloatingLayer>
								)}
							</BuilderMainWithoutSidebar>
						)}}
					/>

					<Route path={'/layout'} render={(route) => {
						const default_description_text = `<h1>${i18n("Brackets")}</h1><p>${i18n("Check the progress of the competition here.")}</p>`;

						const brackets_mosaic_layout = promotion?.config.layout?.brackets_mosaic ?? {
							layout_width:70,
							layout_num_cols:1,
							layout_cols_prop:40,
						};

						const brackets_enabled = props.promotion?.brackets_enabled ?? false;

						return (
							<BuilderMain pm={props.promoModel}>
								<BuilderSidebar onSave={savePromo}>
									<NavigationTabsBuilder
										active={`/${basename}${route.location.pathname}`}
										sections={sections}
									/>

									<ButtonCollection _class={{ other:'not_in_bottom' }}>
										<ToggleSectionButton
											value={brackets_enabled}
											onClick={togglePublicBrackets}
											label_on="This page is visible"
											label_off="This page is not visible"
										/>
									</ButtonCollection>

									<Model model={props.promotion} change={simplePromoChange}>
										<MediaUploader
											auto_url={`/promotions/auto_img/${promotion.id}`}
											auto_type='main'
											_class={{first:true}}
											imageField="brackets_mosaic_image"
											videoField="brackets_mosaic_video"
											full_name="full_brackets_mosaic_image"
											image={props.promotion.full_brackets_mosaic_image}
											video={props.promotion.mosaic_video}
										>Upload media</MediaUploader>

										<RichPlaceHolder
											placeholder={default_description_text}
											name="brackets_mosaic_description"
											has_emojis={true}
										>Introduction message</RichPlaceHolder>
									</Model>

									<div class="gui_not_in_top">
										<ContainerSliders
											section="brackets_mosaic"
											layout={brackets_mosaic_layout}
											change={simplePromoChange}
											display_only_width_slider={true}
										/>
									</div>
								</BuilderSidebar>

								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									<Layout
										promo={props.promotion}
										menus={props.promoModel.Custom__Menus}
										is_builder={true}
										page="brackets_mosaic"
									>
										<PromotionBracketsMosaic
											basename={basename + route.location.pathname}
											brackets={props.brackets.brackets}
											candidates={props.brackets.candidates}
											matches={props.brackets.matches}
											is_builder={true}
											promo={promotion}
										/>
									</Layout>
								</BuilderCanvas>
							</BuilderMain>
						)
					}} />

					<Route path={'/manager/1'} exact={true} render={(route) => {
						return (
							<StageManagerEdit
								pm={props.promotion}
								promoChange={simplePromoChange}
								stages={props.stages}
								stageStore={props.stageStore}
								questionsStore={props.questionsStore}
								savePromo={savePromo}
								sections={sections}
								section_main_path={basename}
								promoModel={pm}
								_promoModel={props.promoModel}
							/>
						)
					}} />

					<Route path={'/advanced'} exact={true} render={(route) => {
						if(props.promotion.promo_type != '2') window.location.hash = basename;

						const default_script_placeholder = `<script type="text/javascript">\n\tdocument.addEventListener("DOMContentLoaded", function() {\n\t\t/* ${i18n("Your code goes here")}... */\n\t});\n</script>`;

						return (
							<BuilderMain pm={props.promoModel}>
								<BuilderSidebar onSave={savePromo}>
									<NavigationTabsBuilder active={`/${basename}${route.location.pathname}`} sections={sections} />

									<Model model={props.promotion} change={simplePromoChange}>
										<TextAreaPlaceHolder
											name="brackets_mosaic_code_tracking"
											zoomable={true}
											is_monospaced={true}
											placeholder={default_script_placeholder}
											_class={{ v_size:'huge', first:true }}
											label_props={{
												tooltip:<Tooltip>In this field you can insert HTML, Javascript or any other code that can be read by a web browser.</Tooltip>,
											}}
										>Javascript, HTML and your own code</TextAreaPlaceHolder>
									</Model>
								</BuilderSidebar>

								<DesignWarningMessage
									promotion={props.promotion}
								/>
							</BuilderMain>
						)
					}} />

					<Route path={'/:bracket_id'} render={(route) => {
						const bracket_id = route.match.params.bracket_id;
						if(bracket_id == 'stages') return null;
						const bracket = props.brackets.brackets.find((b) => b.Bracket.id == bracket_id);
						const candidates = props.brackets.candidates ? props.brackets.candidates.filter((c) => c.BracketCandidate.bracket_id == bracket_id) : [];
						const matches = props.brackets.matches.filter((c) => c.BracketMatch.bracket_id == bracket_id);

						const indexed_matches = {};
						for(let i in matches) {
							let m = matches[i];
							indexed_matches[`${m.BracketMatch.candidateA}-${m.BracketMatch.candidateB}`] = m;
						}

						return (
							<BracketsEdit
								basename={basename}
								bracket={bracket}
								candidates={candidates}
								matches={matches}
								promotion={props.promotion}
								promoModel={props.promoModel}
								stages={props.stages}
								stageStore={props.stageStore}
								questionsStore={props.questionsStore}
							/>
						)}}
					/>
				</Switch>
			</Router>
		</>
	);
};

function select(state) {
	return state;
}

export default connect(select)(injectIntl(BracketsBuilder))